import React, { useEffect, useState, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import WizardStep from '../WizardStep';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { CheckCircle2, AlertTriangle, Check, RefreshCw } from 'lucide-react';
import { Spinner } from '@/components/ui/spinner';
import { supabase } from '@/integrations/supabase/client';
import { stripMcLimitMarker } from '@/lib/mcLimitMarker';

interface SyncStep {
  id: string;
  label: string;
  completed: boolean;
  active: boolean;
}

interface SyncProgressStepProps {
  googleAccountId: string | null;
  onNext: () => void;
  onComplete: (data: { merchantCenterCount: number; feedCount: number }) => void;
}

// @protected — Diese Komponente NICHT ändern! Der aktuelle Flow funktioniert und ist bewusst simpel:
// 1. fetch-account-data aufrufen → 2. Progress pollen → 3. Success
// KEIN registerGcp, KEIN MerchantIdStep, KEINE Registrierungs-Logik.
// Die GCP-Registrierung ist einmalig bei unserem eigenen MC (5764830311) hinterlegt.
// registerGcp für User-MCs aufzurufen würde die Registrierung verschieben und
// unsere API_DEVELOPER-Rolle zerstören (hat am 9. April 2026 einen mehrtägigen Deadlock verursacht).
// Siehe: FEATURES.md → "GCP-Projekt Registrierung" für Details.

const SyncProgressStep: React.FC<SyncProgressStepProps> = ({
  googleAccountId,
  onNext,
  onComplete,
}) => {
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState<string>('pending');
  const [message, setMessage] = useState<string | null>(null);
  const [isPartialCompletion, setIsPartialCompletion] = useState(false);
  const [steps, setSteps] = useState<SyncStep[]>([
    { id: 'token', label: 'Authentication verified', completed: false, active: false },
    { id: 'accounts', label: 'Fetching merchant centers', completed: false, active: false },
    { id: 'feeds', label: 'Loading product feeds', completed: false, active: false },
  ]);

  const triggerDataFetch = useCallback(async () => {
    if (!googleAccountId) return;
    try {
      const { data: { user } } = await supabase.auth.getUser();
      await supabase.functions.invoke('fetch-account-data', {
        body: { googleAccountId, userId: user?.id }
      });
    } catch (error) {
      console.error('Error triggering data fetch:', error);
      Sentry.captureException(error, { tags: { feature: 'sync', action: 'trigger-data-fetch' } });
    }
  }, [googleAccountId]);

  // Trigger data fetch immediately when component mounts
  useEffect(() => {
    triggerDataFetch();
  }, [triggerDataFetch]);

  // Poll sync status
  useEffect(() => {
    if (!googleAccountId) return;

    const interval = setInterval(async () => {
      const { data } = await supabase
        .from('google_accounts')
        .select('sync_status, sync_message, sync_progress')
        .eq('id', googleAccountId)
        .single();

      if (data) {
        setProgress(data.sync_progress || 0);
        setStatus(data.sync_status);
        const currentMessage = data.sync_message || '';
        if (currentMessage) {
          setMessage(currentMessage);
        }

        const isPartial = currentMessage.toLowerCase().includes('partial sync') ||
                          currentMessage.toLowerCase().includes('timeout');
        setIsPartialCompletion(isPartial);

        setSteps(prev => prev.map((step, index) => {
          let completed = false;
          let active = false;

          if (data.sync_status === 'complete') {
            completed = true;
            active = false;
          } else if (data.sync_status === 'fetching') {
            const msgLower = currentMessage.toLowerCase();
            if (msgLower.includes('checking authentication') || msgLower.includes('token') || msgLower.includes('refresh')) {
              active = index === 0;
            } else if (msgLower.includes('fetching merchant') || msgLower.includes('processing merchant')) {
              completed = index <= 0;
              active = index === 1;
            } else if (msgLower.includes('feed') || msgLower.includes('data source')) {
              completed = index <= 1;
              active = index === 2;
            } else {
              active = index === 0;
            }
          } else if (data.sync_status === 'pending') {
            completed = false;
            active = index === 0;
          }

          return { ...step, completed, active };
        }));

        if (data.sync_status === 'complete') {
          clearInterval(interval);

          const { data: merchantCenters } = await supabase
            .from('merchant_centers')
            .select('id')
            .eq('google_account_id', googleAccountId);

          const merchantCenterIds = merchantCenters?.map(mc => mc.id) || [];

          const { count: feedsCount } = await supabase
            .from('feeds')
            .select('*', { count: 'exact', head: true })
            .in('merchant_center_id', merchantCenterIds);

          onComplete({
            merchantCenterCount: merchantCenters?.length || 0,
            feedCount: feedsCount || 0,
          });

          setTimeout(onNext, 1500);
        }

        if (data.sync_status === 'failed') {
          clearInterval(interval);
        }
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [googleAccountId, onNext, onComplete]);

  const handleRetry = async () => {
    if (!googleAccountId) return;
    setStatus('pending');
    setProgress(0);
    setMessage(null);
    setIsPartialCompletion(false);
    triggerDataFetch();
  };

  const getStatusMessage = () => {
    // MC-Anzahl-Gate: sync_message kann den maschinenlesbaren Marker
    // `mc_limit_blocked:<n>` tragen (fetch-account-data). Wie in
    // SyncProgressDialog/GoogleAccounts wird er vor der Anzeige entfernt.
    if (message) return stripMcLimitMarker(message);

    switch (status) {
      case 'pending': return 'Connecting to Google...';
      case 'fetching': return 'Synchronizing data...';
      case 'complete': return 'Synchronization Complete!';
      case 'failed': return 'Synchronization failed. Please try again.';
      default: return 'Processing...';
    }
  };

  return (
    <WizardStep
      title="Syncing Your Data"
      description="Syncing your Merchant Centers and feeds..."
    >
      <div className="space-y-4">
        <div className="flex flex-col items-center justify-center space-y-3">
          {status === 'complete' ? (
            <CheckCircle2 className="w-12 h-12 text-primary" />
          ) : status === 'failed' ? (
            <AlertTriangle className="w-12 h-12 text-destructive" />
          ) : (
            <Spinner size="xl" />
          )}

          <div className="text-center w-full">
            <p className="text-sm font-medium">{getStatusMessage()}</p>

            <div className="space-y-1.5 text-left max-w-sm mx-auto mt-4">
              {steps.map((step) => (
                <div key={step.id} className="flex items-center gap-2.5 py-1">
                  <div className={`flex-shrink-0 w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                    step.completed
                      ? 'bg-primary border-primary'
                      : step.active
                        ? 'border-primary'
                        : 'border-muted'
                  }`}>
                    {step.completed ? (
                      <Check className="w-2.5 h-2.5 text-primary-foreground" />
                    ) : step.active ? (
                      <Spinner size="sm" />
                    ) : null}
                  </div>
                  <span className={`text-sm ${
                    step.completed || step.active ? 'text-foreground' : 'text-muted-foreground'
                  }`}>
                    {step.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <Progress value={progress} variant="gradient" className="h-2.5" />

        {status === 'complete' && isPartialCompletion && (
          <p className="text-xs text-muted-foreground text-center">
            Some data may not have synced. You can refresh later in Settings.
          </p>
        )}

        {status === 'failed' && (
          <div className="flex justify-center">
            <Button variant="outline" size="sm" onClick={handleRetry}>
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
              Retry
            </Button>
          </div>
        )}
      </div>
    </WizardStep>
  );
};

export default SyncProgressStep;
